import React, { useState, useEffect } from "react";
import { API } from "@/App";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { BarChart3, Eye, Users, Globe, ArrowLeft, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const TrafficPage = () => {
  const { token } = useAdminAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/admin/analytics/traffic?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setData(await res.json());
      } catch {}
      setLoading(false);
    };
    if (token) load();
  }, [token, days]);

  const maxViews = data?.daily_views?.length
    ? Math.max(...data.daily_views.map((d) => d.views))
    : 1;

  const pageLabels = {
    "/": "Hjem",
    "/menu": "Meny",
    "/order": "Bestilling",
    "/bestill": "Bestilling",
    "/booking": "Reservasjon",
    "/gift-cards": "Gavekort",
    "/gift-cards/saldo": "Saldo-sjekk",
    "/catering": "Catering",
    "/gallery": "Galleri",
    "/about": "Om oss",
    "/contact": "Kontakt",
  };

  return (
    <div
      className="min-h-screen pt-24 pb-16"
      style={{ background: "linear-gradient(180deg, #1a0805 0%, #0d0402 100%)" }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <Link
          to="/panel050623"
          className="inline-flex items-center gap-2 mb-6 text-sm hover:underline"
          style={{ color: "#d4a847" }}
        >
          <ArrowLeft className="h-4 w-4" /> Tilbake til admin
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#e8c86a" }}>
              Trafikk-oversikt
            </h1>
            <p className="text-sm mt-1" style={{ color: "#8b7355" }}>
              Besøksstatistikk for masalaindisk.no
            </p>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: days === d ? "#d4a847" : "rgba(212,168,71,0.1)",
                  color: days === d ? "#1a0805" : "#d4a847",
                  border: `1px solid ${days === d ? "#d4a847" : "rgba(212,168,71,0.3)"}`,
                }}
                data-testid={`traffic-period-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p style={{ color: "#8b7355" }}>Laster...</p>
        ) : !data ? (
          <p style={{ color: "#8b7355" }}>Ingen data tilgjengelig</p>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <StatCard icon={Eye} label="Sidevisninger" value={data.total_views} />
              <StatCard icon={Users} label="Unike besøkende" value={data.unique_visitors} color="#22c55e" />
              <StatCard
                icon={TrendingUp}
                label="Snitt per dag"
                value={data.period_days > 0 ? Math.round(data.total_views / data.period_days) : 0}
                color="#f59e0b"
              />
            </div>

            {/* Daily chart */}
            <div
              className="rounded-xl p-6 mb-8"
              style={{ background: "rgba(45,24,16,0.6)", border: "1px solid rgba(212,168,71,0.2)" }}
            >
              <h2 className="text-sm font-semibold mb-4" style={{ color: "#d4a847" }}>
                Daglige sidevisninger
              </h2>
              {data.daily_views.length === 0 ? (
                <p className="text-sm" style={{ color: "#8b7355" }}>Ingen data ennå</p>
              ) : (
                <div className="flex items-end gap-1 h-40 overflow-x-auto" data-testid="daily-chart">
                  {data.daily_views.map((d) => (
                    <div key={d.date} className="flex flex-col items-center flex-shrink-0" style={{ minWidth: 24 }}>
                      <span className="text-[9px] mb-1" style={{ color: "#8b7355" }}>{d.views}</span>
                      <div
                        className="w-4 rounded-t transition-all"
                        style={{
                          height: `${Math.max((d.views / maxViews) * 120, 4)}px`,
                          background: "linear-gradient(to top, #a08030, #e8c86a)",
                        }}
                      />
                      <span className="text-[8px] mt-1 rotate-[-45deg] origin-top-left" style={{ color: "#6b5c45" }}>
                        {d.date.slice(5)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top pages + referrers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div
                className="rounded-xl p-6"
                style={{ background: "rgba(45,24,16,0.6)", border: "1px solid rgba(212,168,71,0.2)" }}
              >
                <h2 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: "#d4a847" }}>
                  <BarChart3 className="h-4 w-4" /> Mest besøkte sider
                </h2>
                <div className="space-y-2">
                  {data.top_pages.map((p) => (
                    <div key={p.page} className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: "#fff8e7" }}>
                        {pageLabels[p.page] || p.page}
                      </span>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-2 rounded-full overflow-hidden" style={{ background: "rgba(212,168,71,0.1)" }}>
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${(p.views / (data.top_pages[0]?.views || 1)) * 100}%`,
                              background: "#d4a847",
                            }}
                          />
                        </div>
                        <span className="text-xs font-semibold w-8 text-right" style={{ color: "#e8c86a" }}>
                          {p.views}
                        </span>
                      </div>
                    </div>
                  ))}
                  {data.top_pages.length === 0 && (
                    <p className="text-sm" style={{ color: "#8b7355" }}>Ingen data ennå</p>
                  )}
                </div>
              </div>

              <div
                className="rounded-xl p-6"
                style={{ background: "rgba(45,24,16,0.6)", border: "1px solid rgba(212,168,71,0.2)" }}
              >
                <h2 className="text-sm font-semibold mb-4 flex items-center gap-2" style={{ color: "#d4a847" }}>
                  <Globe className="h-4 w-4" /> Trafikk-kilder
                </h2>
                <div className="space-y-2">
                  {data.top_referrers.map((r) => (
                    <div key={r.referrer} className="flex items-center justify-between">
                      <span className="text-xs truncate max-w-[200px]" style={{ color: "#fff8e7" }}>
                        {r.referrer}
                      </span>
                      <span className="text-xs font-semibold" style={{ color: "#e8c86a" }}>
                        {r.views}
                      </span>
                    </div>
                  ))}
                  {data.top_referrers.length === 0 && (
                    <p className="text-sm" style={{ color: "#8b7355" }}>Ingen eksterne kilder ennå</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color = "#d4a847" }) => (
  <div
    className="rounded-xl p-5"
    style={{ background: "rgba(45,24,16,0.6)", border: "1px solid rgba(212,168,71,0.2)" }}
  >
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg" style={{ background: `${color}15` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs" style={{ color: "#8b7355" }}>{label}</p>
        <p className="text-xl font-bold" style={{ color }}>{value}</p>
      </div>
    </div>
  </div>
);

export default TrafficPage;
